import * as React from "react";
import { useEffect, useState } from "react";
import { BottomSheet } from "../../../calendar/BottomSheet";
import { toIsoDate } from "../../detail/twvFrist";
import { twvPatch } from "../../shared/fristZurTwv";
import { useSheetKlasse } from "./sheetKlasse";

/**
 * FRISTEN AUF DEM HANDY (Plan „Vorgangsdetail mobil", PR 7, §3e): Die
 * Frist-Pille der To-do-Zeile öffnet dieses Sheet mit den NATIVEN
 * Datumsfeldern des Geräts (iOS/Android bringen dafür ihr eigenes Rad bzw.
 * Kalenderblatt mit) — statt des Kalender-Popovers vom Desktop.
 *
 * Es gilt dieselbe Regel wie dort (`twvPatch`, T-331): Wird die TWV gesetzt,
 * rückt eine leere oder frühere Frist auf die TWV nach.
 */
export type FristWerte = { twv: string | null; frist: string | null };

/** Was sich geändert hat, als Patch — leer = nichts zu speichern. */
export function fristSheetPatch(alt: FristWerte, neu: FristWerte): Partial<FristWerte> {
    const a = { twv: toIsoDate(alt.twv) || null, frist: toIsoDate(alt.frist) || null };
    const n = { twv: neu.twv || null, frist: neu.frist || null };
    const patch: Partial<FristWerte> = {};
    if (n.frist !== a.frist) patch.frist = n.frist;
    if (n.twv !== a.twv) Object.assign(patch, twvPatch(n.twv, n.frist));
    return patch;
}

export const FristSheet: React.FC<{
    offen: boolean;
    titel: string;
    werte: FristWerte;
    onSchliessen: () => void;
    onSpeichern: (patch: Partial<FristWerte>) => void | Promise<void>;
}> = ({ offen, titel, werte, onSchliessen, onSpeichern }) => {
    const [twv, setTwv] = useState("");
    const [frist, setFrist] = useState("");
    const [busy, setBusy] = useState(false);
    useSheetKlasse(offen);
    useEffect(() => {
        if (!offen) return;
        setTwv(toIsoDate(werte.twv));
        setFrist(toIsoDate(werte.frist));
    }, [offen, werte.twv, werte.frist]);

    const speichern = async () => {
        const patch = fristSheetPatch(werte, { twv: twv || null, frist: frist || null });
        if (Object.keys(patch).length === 0) return onSchliessen();
        setBusy(true);
        try {
            await onSpeichern(patch);
            onSchliessen();
        } finally {
            setBusy(false);
        }
    };

    return (
        <BottomSheet
            open={offen}
            title="Fristen"
            onClose={onSchliessen}
            footer={
                <>
                    <button type="button" className="vg4m-frist-weg" onClick={onSchliessen}>
                        Abbrechen
                    </button>
                    <button type="button" className="vg4m-weiter vg4m-frist-ok" disabled={busy} onClick={() => void speichern()}>
                        {busy ? "Speichert …" : "Speichern"}
                    </button>
                </>
            }
        >
            <div className="vg4m-plus-ziel">
                für <strong>„{titel}“</strong>
            </div>
            <label className="ct-sheet-fld">
                <span className="ct-sheet-lbl">Wiedervorlage (TWV)</span>
                <span className="vg4m-datum-reihe">
                    <input className="vg4m-datum" type="date" value={twv} onChange={(e) => setTwv(e.target.value)} />
                    {twv && (
                        <button type="button" className="vg4m-datum-leeren" onClick={() => setTwv("")}>
                            Leeren
                        </button>
                    )}
                </span>
            </label>
            <label className="ct-sheet-fld">
                <span className="ct-sheet-lbl">Frist</span>
                <span className="vg4m-datum-reihe">
                    <input className="vg4m-datum" type="date" value={frist} onChange={(e) => setFrist(e.target.value)} />
                    {frist && (
                        <button type="button" className="vg4m-datum-leeren" onClick={() => setFrist("")}>
                            Leeren
                        </button>
                    )}
                </span>
            </label>
            <p className="vg4m-plus-hilfe">Liegt die Frist vor der Wiedervorlage, rückt sie auf die Wiedervorlage nach.</p>
        </BottomSheet>
    );
};
